/**
 * Form validation helpers: email (regex), phone (US 10/11 digit), full name.
 */

import type { PasswordPolicy } from '@lambda/shared/types';

import {
  DEFAULT_PASSWORD_POLICY,
  getPasswordRequirementChecks as getChecks,
  validatePasswordWithPolicy,
} from './passwordPolicy';
import { getPhoneDigits } from './phoneUtils';

/** Permissive email regex: non-whitespace local part, @, domain with at least one dot. */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns true if the string looks like a valid email (local@domain).
 */
export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  return EMAIL_REGEX.test(trimmed);
}

/** US phone: 10 digits, or 11 digits with leading 1. */
const PHONE_DIGITS_10 = 10;
const PHONE_DIGITS_11 = 11;
const US_COUNTRY_CODE = '1';

/**
 * Returns true if the string is a valid US phone (10 digits or 1 + 10 digits).
 */
export function isValidPhone(value: string): boolean {
  const digits = getPhoneDigits(value);
  if (digits.length === PHONE_DIGITS_10) return true;
  if (digits.length === PHONE_DIGITS_11 && digits.startsWith(US_COUNTRY_CODE)) return true;
  return false;
}

/**
 * Returns true if the string looks like a full name (at least two non-empty parts).
 * Trims and splits on whitespace; each part should be at least 1 character.
 */
export function isFullName(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length < 2) return false;
  const parts = trimmed.split(/\s+/).filter((p) => p.length > 0);
  return parts.length >= 2;
}

/** Password rules (uses site policy when provided). */
export function validatePassword(
  value: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY
): { valid: boolean; message?: string } {
  return validatePasswordWithPolicy(value, policy);
}

/** Per-rule state for live password requirements checklist. */
export function getPasswordRequirementChecks(
  value: string,
  policy: PasswordPolicy = DEFAULT_PASSWORD_POLICY
): { id: string; label: string; met: boolean }[] {
  return getChecks(value, policy);
}
